import { useRef, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { uploadExpenseFile, startImport, readImportFile, analyzeImport } from "../../api/expenses";
import { shrinkImage } from "../../utils/files";

// Upload a pile of statements (bank, card, Amazon; PDF, screenshot or CSV).
// Each file is read, you confirm which account it's from, then everything is
// matched, de-duplicated and sorted into the Review tab. Nothing is saved as
// an expense until you answer or add it there.

const KIND = { statement: "Statement", amazon: "Amazon orders", receipt: "Receipt" };

export default function StatementsModal({ data, onClose }) {
  const [files, setFiles] = useState([]); // {name, status, lines, file_id, account_id, document_type, error}
  const [stage, setStage] = useState("pick"); // pick | reading | confirm | sorting
  const [error, setError] = useState("");
  const pickRef = useRef(null);
  const batchRef = useRef(null);

  const update = (i, patch) => setFiles((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  async function pick(e) {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    setError("");
    setStage("reading");
    const start = files.length;
    setFiles((fs) => [...fs, ...chosen.map((f) => ({ name: f.name, status: "waiting" }))]);
    try {
      if (!batchRef.current) batchRef.current = (await startImport()).batch_id;
    } catch (err) {
      setError(err.message || "Couldn't start.");
      setStage("pick");
      return;
    }
    // One file at a time keeps each request short and shows steady progress.
    for (let k = 0; k < chosen.length; k++) {
      const i = start + k;
      update(i, { status: "uploading" });
      try {
        const up = await uploadExpenseFile(await shrinkImage(chosen[k]));
        update(i, { status: "reading", file_id: up.id });
        const r = await readImportFile(batchRef.current, up.id);
        update(i, { status: "done", lines: r.lines, document_type: r.document_type, account_id: r.account_id || "", hint: r.account_hint });
      } catch (err) {
        update(i, { status: "error", error: err.message || "Couldn't read it." });
      }
    }
    setStage("confirm");
  }

  const done = files.filter((f) => f.status === "done");
  const lines = done.reduce((t, f) => t + (f.lines || 0), 0);
  const unknownAccount = done.some((f) => f.document_type !== "amazon" && !f.account_id);

  async function sort() {
    setStage("sorting");
    setError("");
    try {
      const r = await analyzeImport(
        batchRef.current,
        done.map((f) => ({ file_id: f.file_id, account_id: f.account_id || null }))
      );
      onClose(true, r.result);
    } catch (err) {
      setError(err.message || "Couldn't sort them.");
      setStage("confirm");
    }
  }

  const footer =
    stage === "confirm" && done.length > 0 ? (
      <div className="space-y-2">
        {unknownAccount && <p className="text-xs text-amber-700">Pick the account for each statement so the right card gets each charge.</p>}
        <Button onClick={sort} className="w-full py-3">
          Sort {lines} line{lines === 1 ? "" : "s"} →
        </Button>
      </div>
    ) : null;

  return (
    <Modal isOpen title="Add statements" onClose={() => onClose(false)} footer={footer} maxWidthClass="max-w-xl">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <input
        ref={pickRef}
        type="file"
        multiple
        accept="image/*,application/pdf,.csv,text/csv"
        className="hidden"
        onChange={pick}
      />

      {stage === "sorting" ? (
        <div className="text-center py-12 space-y-2">
          <div className="text-4xl animate-pulse">🧠</div>
          <p className="text-sm font-semibold text-birdie-charcoal">Sorting everything…</p>
          <p className="text-xs text-birdie-charcoal/55">
            Matching Amazon orders to card charges, spotting things you already have, and picking categories.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {files.length === 0 && (
            <div className="text-center py-4 space-y-3">
              <div className="text-4xl">🏦</div>
              <p className="text-sm text-birdie-charcoal/70">
                Bank and credit card statements, and your Amazon order history. PDFs, screenshots or the CSV download all
                work, and you can pick several at once.
              </p>
              <p className="text-xs text-birdie-charcoal/50">
                Amazon: Account → Your Orders (screenshots), or Account → Request your data → Your Orders (CSV).
              </p>
            </div>
          )}
          {files.length > 0 && (
            <ul className="space-y-2">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="rounded-xl border border-birdie-green/15 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 truncate text-sm font-medium text-birdie-charcoal">📄 {f.name}</span>
                    <span className="shrink-0 text-xs text-birdie-charcoal/55">
                      {f.status === "done"
                        ? `${f.lines} line${f.lines === 1 ? "" : "s"}`
                        : f.status === "error"
                          ? ""
                          : f.status === "waiting"
                            ? "Waiting"
                            : f.status === "uploading"
                              ? "Uploading…"
                              : "Reading…"}
                    </span>
                  </div>
                  {f.status === "error" && <p className="text-xs text-red-700 mt-1">{f.error}</p>}
                  {f.status === "done" &&
                    (f.document_type === "amazon" ? (
                      <p className="text-xs text-birdie-charcoal/55 mt-1">
                        {KIND.amazon}: each order gets matched to the card charge that paid for it.
                      </p>
                    ) : (
                      <label className="flex items-center gap-2 mt-2 text-xs">
                        <span className="shrink-0 text-birdie-charcoal/60">{KIND[f.document_type] || "Statement"} from</span>
                        <select
                          value={f.account_id}
                          onChange={(e) => update(i, { account_id: e.target.value })}
                          className={`min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs bg-white ${
                            f.account_id ? "border-birdie-green/20" : "border-amber-300 text-amber-800"
                          }`}
                        >
                          <option value="">Which account?{f.hint ? ` (it says “${f.hint}”)` : ""}</option>
                          {data.accounts
                            .filter((a) => !a.archived)
                            .map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name}
                                {a.owner !== "company" ? ` (${a.owner}'s)` : ""}
                              </option>
                            ))}
                        </select>
                      </label>
                    ))}
                </li>
              ))}
            </ul>
          )}
          <div className="text-center">
            <Button variant={files.length ? "secondary" : "primary"} onClick={() => pickRef.current?.click()} disabled={stage === "reading"}>
              {stage === "reading" ? "Reading…" : files.length ? "+ Add more files" : "Choose files"}
            </Button>
          </div>
          {stage === "reading" && (
            <p className="text-center text-xs text-birdie-charcoal/50">
              A statement takes up to a minute to read. Keep this open.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
